import React, { useEffect, useState } from "react";
import { useWalletConnect } from "@btc-vision/walletconnect";
import {
  useAllEscrows,
  useUserReputation,
  useTokenMetadata,
  formatTokenAmount,
} from "../hooks/useEscrow";

/**
 * Reputation score bands match the on-chain _computeScore() in UserReputation.ts:
 *   base = min(trades × 10, 4000)
 *   winBonus = min(dWon × 50, 3000)
 *   raw = base + winBonus − dLost × 100   (clamped 0..10000)
 */
function scoreBand(score: number): { label: string; color: string; bgColor: string } {
  if (score >= 7000) return { label: "Excelente", color: "#22c55e", bgColor: "rgba(34,197,94,0.15)" };
  if (score >= 4000) return { label: "Bueno", color: "#60a5fa", bgColor: "rgba(96,165,250,0.15)" };
  if (score >= 1500) return { label: "Regular", color: "#eab308", bgColor: "rgba(234,179,8,0.15)" };
  if (score > 0) return { label: "Novato", color: "#f97316", bgColor: "rgba(249,115,22,0.15)" };
  return { label: "Sin historia", color: "var(--text-muted)", bgColor: "rgba(255,255,255,0.05)" };
}

function truncateAddr(addr: string): string {
  if (!addr) return "---";
  if (addr.length <= 20) return addr;
  return `${addr.slice(0, 14)}...${addr.slice(-8)}`;
}

const STORAGE_KEY = "motosettle_watched_users";

function loadWatched(): string[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function saveWatched(addrs: string[]) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(addrs));
}

export function UserReputationPage() {
  const { address, walletAddress } = useWalletConnect();
  const walletHex = (address as any)?.toString?.() ?? walletAddress ?? "";

  // My reputation
  const { data: myRep, isLoading: myLoading } = useUserReputation(walletHex || undefined);

  // Watched users
  const [watched, setWatched] = useState<string[]>(() => loadWatched());
  const [searchAddr, setSearchAddr] = useState("");
  const [searchError, setSearchError] = useState("");

  // Escrows — show my trade history
  const { data: escrows } = useAllEscrows();
  const firstToken = escrows?.find((e) => !/^0x0+$/.test(e.token.toLowerCase()))?.token;
  const { data: tokenMeta } = useTokenMetadata(firstToken);
  const decimals = tokenMeta?.decimals ?? 8;
  const symbol = tokenMeta?.symbol ?? "tokens";

  useEffect(() => {
    saveWatched(watched);
  }, [watched]);

  const addWatched = () => {
    const a = searchAddr.trim();
    if (!a) return;
    setSearchError("");
    if (watched.includes(a)) {
      setSearchError("Ya esta en la lista.");
      return;
    }
    setWatched((prev) => [...prev, a]);
    setSearchAddr("");
  };

  const removeWatched = (addr: string) => {
    setWatched((prev) => prev.filter((a) => a !== addr));
  };

  // Count my roles across escrows
  const myHex = walletHex.toLowerCase();
  const myTrades = (escrows ?? []).filter((e) => {
    const match = (f: string) => f.toLowerCase() === myHex;
    return match(e.buyer) || match(e.seller) || match(e.arbitrator);
  });
  const asBuyer = myTrades.filter((e) => e.buyer.toLowerCase() === myHex).length;
  const asSeller = myTrades.filter((e) => e.seller.toLowerCase() === myHex).length;
  const asArbitrator = myTrades.filter((e) => e.arbitrator.toLowerCase() === myHex).length;

  if (!walletAddress) {
    return (
      <div className="page">
        <div className="page-header">
          <h1>User Reputation</h1>
        </div>
        <div className="alert alert-info" style={{ maxWidth: 700 }}>
          Conecta tu wallet para consultar tu reputacion on-chain.
        </div>
      </div>
    );
  }

  return (
    <div className="page">
      <div className="page-header">
        <h1>User Reputation</h1>
        <p>
          Reputacion verificable on-chain basada en trades completados y resoluciones de disputas.
          Los datos vienen directamente del contrato <code>UserReputation</code>.
        </p>
      </div>

      {/* ─── My Reputation ──────────────────────────────────────── */}
      <div className="card" style={{ maxWidth: 900, marginBottom: 24 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <h3 style={{ margin: 0 }}>Mi Reputacion</h3>
          {myLoading && <span style={{ fontSize: 13, color: "var(--text-secondary)" }}>Cargando...</span>}
        </div>

        {myRep ? (
          <ReputationPanel
            rep={myRep}
            addr={walletHex}
            decimals={decimals}
            symbol={symbol}
            asBuyer={asBuyer}
            asSeller={asSeller}
            asArbitrator={asArbitrator}
          />
        ) : !myLoading ? (
          <div style={{ color: "var(--text-muted)", fontSize: 14 }}>
            No hay datos de reputacion. Completa tu primer trade para construir historial.
          </div>
        ) : null}
      </div>

      {/* ─── Watch other users ──────────────────────────────────── */}
      <div className="card" style={{ maxWidth: 900, marginBottom: 24 }}>
        <h3 style={{ marginTop: 0 }}>Buscar Reputacion de un Usuario</h3>
        <div style={{ display: "flex", gap: 8 }}>
          <input
            type="text"
            placeholder="Direccion OP_NET (opt1... o 0x...)"
            value={searchAddr}
            onChange={(e) => setSearchAddr(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && addWatched()}
            style={{
              flex: 1,
              padding: "8px 12px",
              borderRadius: 6,
              border: "1px solid rgba(255,255,255,0.15)",
              background: "rgba(255,255,255,0.05)",
              color: "inherit",
              fontSize: 14,
            }}
          />
          <button className="btn btn-primary" onClick={addWatched} disabled={!searchAddr.trim()}>
            Agregar
          </button>
        </div>
        {searchError && (
          <p style={{ color: "#f87171", marginTop: 8, fontSize: 13 }}>{searchError}</p>
        )}
      </div>

      {watched.length > 0 && (
        <div style={{ maxWidth: 900 }}>
          <h3 style={{ marginTop: 0 }}>Usuarios Observados</h3>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {watched.map((addr) => (
              <WatchedUserCard
                key={addr}
                addr={addr}
                decimals={decimals}
                symbol={symbol}
                onRemove={() => removeWatched(addr)}
              />
            ))}
          </div>
        </div>
      )}

      {/* ─── Scoring explainer ──────────────────────────────────── */}
      <div
        className="card"
        style={{
          maxWidth: 900,
          marginTop: 24,
          background: "rgba(255,255,255,0.02)",
        }}
      >
        <h3 style={{ marginTop: 0, fontSize: 14 }}>Como se calcula el score?</h3>
        <p style={{ fontSize: 12, color: "var(--text-muted)", margin: "4px 0" }}>
          Formula on-chain (<code>UserReputation._computeScore</code>):
        </p>
        <pre
          style={{
            fontSize: 11,
            color: "var(--text-secondary)",
            background: "rgba(0,0,0,0.3)",
            padding: 12,
            borderRadius: 6,
            margin: "8px 0",
          }}
        >
{`base     = min(completedTrades × 10, 4000)
winBonus = min(disputesWon    × 50, 3000)
penalty  =     disputesLost   × 100
score    = clamp(base + winBonus − penalty, 0..10000)`}
        </pre>
        <p style={{ fontSize: 12, color: "var(--text-muted)", margin: "4px 0" }}>
          Bandas: <strong style={{ color: "#22c55e" }}>Excelente</strong> (7000+) ·{" "}
          <strong style={{ color: "#60a5fa" }}>Bueno</strong> (4000+) ·{" "}
          <strong style={{ color: "#eab308" }}>Regular</strong> (1500+) ·{" "}
          <strong style={{ color: "#f97316" }}>Novato</strong> (&gt;0)
        </p>
      </div>
    </div>
  );
}

// ─── Subcomponents ──────────────────────────────────────────────────────────

function ReputationPanel({
  rep,
  addr,
  decimals,
  symbol,
  asBuyer,
  asSeller,
  asArbitrator,
}: {
  rep: import("../hooks/useEscrow").UserReputationStats;
  addr: string;
  decimals: number;
  symbol: string;
  asBuyer?: number;
  asSeller?: number;
  asArbitrator?: number;
}) {
  const score = Number(rep.repScore);
  const band = scoreBand(score);
  const scorePct = Math.min(100, (score / 10000) * 100);

  const disputeRate =
    rep.completedTrades > 0n
      ? ((Number(rep.disputesRaised) / Number(rep.completedTrades)) * 100).toFixed(1) + "%"
      : "—";

  const disputeWinRate =
    rep.disputesWon + rep.disputesLost > 0n
      ? (
          (Number(rep.disputesWon) / Number(rep.disputesWon + rep.disputesLost)) *
          100
        ).toFixed(0) + "%"
      : "—";

  return (
    <div>
      {/* Address + score band */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
        <span
          style={{
            padding: "4px 12px",
            borderRadius: 4,
            fontSize: 12,
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: "0.05em",
            background: band.bgColor,
            color: band.color,
          }}
        >
          {band.label}
        </span>
        <span
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: 12,
            color: "var(--text-secondary)",
            wordBreak: "break-all",
          }}
        >
          {addr}
        </span>
      </div>

      {/* Score bar */}
      <div style={{ marginBottom: 20 }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            fontSize: 12,
            color: "var(--text-muted)",
            marginBottom: 6,
          }}
        >
          <span>Score</span>
          <span style={{ color: band.color, fontWeight: 700, fontSize: 14 }}>
            {score.toLocaleString()} / 10,000
          </span>
        </div>
        <div
          style={{
            height: 10,
            background: "rgba(255,255,255,0.05)",
            borderRadius: 5,
            overflow: "hidden",
          }}
        >
          <div
            style={{
              width: `${scorePct}%`,
              height: "100%",
              background: `linear-gradient(90deg, ${band.color}aa, ${band.color})`,
              transition: "width 0.3s",
            }}
          />
        </div>
      </div>

      {/* Stats grid */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))",
          gap: 12,
          marginBottom: 16,
        }}
      >
        <StatBox label="Trades" value={rep.completedTrades.toString()} />
        <StatBox
          label={`Volumen (${symbol})`}
          value={formatTokenAmount(rep.lifetimeVolume, decimals)}
        />
        <StatBox label="Disputas" value={rep.disputesRaised.toString()} />
        <StatBox label="Ganadas" value={rep.disputesWon.toString()} color="#22c55e" />
        <StatBox label="Perdidas" value={rep.disputesLost.toString()} color="#ef4444" />
        <StatBox label="% Disputas" value={disputeRate} />
        <StatBox label="Win Rate disp." value={disputeWinRate} />
      </div>

      {/* Optional role counts when present */}
      {(asBuyer !== undefined || asSeller !== undefined || asArbitrator !== undefined) && (
        <div
          style={{
            display: "flex",
            gap: 12,
            paddingTop: 12,
            borderTop: "1px solid rgba(255,255,255,0.06)",
            fontSize: 12,
            color: "var(--text-muted)",
            flexWrap: "wrap",
          }}
        >
          <span>
            Como buyer: <strong style={{ color: "var(--text-primary)" }}>{asBuyer ?? 0}</strong>
          </span>
          <span>·</span>
          <span>
            Como seller: <strong style={{ color: "var(--text-primary)" }}>{asSeller ?? 0}</strong>
          </span>
          <span>·</span>
          <span>
            Como arbitro: <strong style={{ color: "var(--text-primary)" }}>{asArbitrator ?? 0}</strong>
          </span>
        </div>
      )}
    </div>
  );
}

function WatchedUserCard({
  addr,
  decimals,
  symbol,
  onRemove,
}: {
  addr: string;
  decimals: number;
  symbol: string;
  onRemove: () => void;
}) {
  const { data: rep, isLoading, error } = useUserReputation(addr);

  const score = rep ? Number(rep.repScore) : 0;
  const band = scoreBand(score);

  return (
    <div
      className="card"
      style={{
        borderLeft: `3px solid ${band.color}`,
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6, flexWrap: "wrap" }}>
            <span
              style={{
                padding: "2px 8px",
                borderRadius: 4,
                fontSize: 10,
                fontWeight: 700,
                textTransform: "uppercase",
                background: band.bgColor,
                color: band.color,
              }}
            >
              {band.label}
            </span>
            {rep && (
              <span style={{ fontSize: 12, fontWeight: 600, color: band.color }}>
                Score {score.toLocaleString()}
              </span>
            )}
          </div>
          <div
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: 11,
              color: "var(--text-secondary)",
              wordBreak: "break-all",
            }}
          >
            {truncateAddr(addr)}
          </div>
        </div>
        <button
          className="btn btn-ghost btn-sm"
          style={{ fontSize: 11, padding: "4px 8px" }}
          onClick={onRemove}
          title="Quitar"
        >
          x
        </button>
      </div>

      {isLoading && (
        <div style={{ marginTop: 12, fontSize: 12, color: "var(--text-muted)" }}>Cargando...</div>
      )}

      {error && (
        <div style={{ marginTop: 12, fontSize: 12, color: "#f87171" }}>
          Error: {(error as Error).message}
        </div>
      )}

      {rep && (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(5, 1fr)",
            gap: 8,
            textAlign: "center",
            marginTop: 12,
            padding: "12px 0 0",
            borderTop: "1px solid rgba(255,255,255,0.06)",
          }}
        >
          <StatBox small label="Trades" value={rep.completedTrades.toString()} />
          <StatBox
            small
            label={`Vol (${symbol})`}
            value={formatTokenAmount(rep.lifetimeVolume, decimals)}
          />
          <StatBox small label="Disputas" value={rep.disputesRaised.toString()} />
          <StatBox small label="Won" value={rep.disputesWon.toString()} color="#22c55e" />
          <StatBox small label="Lost" value={rep.disputesLost.toString()} color="#ef4444" />
        </div>
      )}
    </div>
  );
}

function StatBox({
  label,
  value,
  color,
  small,
}: {
  label: string;
  value: number | string;
  color?: string;
  small?: boolean;
}) {
  return (
    <div style={{ textAlign: "center" }}>
      <div
        style={{
          fontSize: small ? 14 : 20,
          fontWeight: 700,
          color: color ?? "var(--text-primary)",
          fontFamily: "var(--font-mono)",
        }}
      >
        {value}
      </div>
      <div style={{ fontSize: small ? 10 : 11, color: "var(--text-secondary)", marginTop: 2 }}>
        {label}
      </div>
    </div>
  );
}
